/**
 * Ad-Hoc Interest Payment Dialog
 * Allows requesting ad-hoc interest payment events on Active contracts.
 * Creates a pending_changes entry for approval workflow.
 */

import React, { useState, useEffect } from 'react';
import { TextField, Grid, InputAdornment } from '@mui/material';
import { format } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import ContractActionDialogBase from './ContractActionDialogBase';
import ScheduleImpactPreviewModal from './ScheduleImpactPreviewModal';

const AdhocInterestPaymentDialog = ({ open, onClose, contract, contractId, onSuccess }) => {
  const [formData, setFormData] = useState({
    event_date: format(new Date(), 'yyyy-MM-dd'),
    description: '',
    amount: '',
  });
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        event_date: format(new Date(), 'yyyy-MM-dd'),
        description: '',
        amount: '',
      });
      setReason('');
      setError(null);
      setPreviewData(null);
      setPreviewModalOpen(false);
    }
  }, [open]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (['event_date', 'amount'].includes(field)) {
      setPreviewData(null);
    }
  };

  const handlePreview = async () => {
    if (!formData.event_date) return;
    const cid = contractId || contract?.id;
    if (!cid) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ event_date: formData.event_date });
      if (formData.amount !== '') params.append('amount', formData.amount);
      const response = await apiGet(`/amendments/contract/${cid}/interest-preview?${params.toString()}`);
      if (response?.success) {
        setPreviewData(response.data);
        setPreviewModalOpen(true);
      } else {
        setError(response?.message || 'Failed to generate preview');
      }
    } catch (err) {
      setError('Failed to generate preview: ' + (err.response?.data?.message || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async ({ proof_document_ids }) => {
    if (!formData.event_date) {
      setError('Event date is required');
      return;
    }
    if (contract?.maturity_date && new Date(formData.event_date) >= new Date(contract.maturity_date)) {
      setError('Event date must be before the contract maturity date');
      return;
    }
    if (contract?.deposit_date && new Date(formData.event_date) <= new Date(contract.deposit_date)) {
      setError('Event date must be after the contract deposit date');
      return;
    }
    let parsedAmount = null;
    if (formData.amount !== '') {
      parsedAmount = parseFloat(formData.amount);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        setError('Interest amount must be a positive number');
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiPost(`/contracts/${contractId || contract?.id}/adhoc-interest-payments`, {
        event_date: formData.event_date,
        description: formData.description || null,
        motivation: reason,
        amount: parsedAmount,
        proof_document_ids,
      });
      if (response?.success) {
        onClose();
        if (onSuccess) onSuccess();
      } else {
        setError(response?.message || 'Failed to submit interest payment request');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit interest payment request');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = !!formData.event_date;

  return (
    <>
    <ContractActionDialogBase
      open={open}
      onClose={onClose}
      contract={contract}
      title="Ad-Hoc Interest Payment"
      description="Add an interest payment to the amortization schedule. Specify an amount or leave blank to auto-calculate accrued interest. The schedule will be regenerated upon approval."
      submitLabel="Submit for Approval"
      submitColor="info"
      isValid={isValid}
      submitting={submitting}
      error={error}
      onClearError={() => setError(null)}
      onSubmit={handleSubmit}
      onPreview={handlePreview}
      showPreview={true}
      previewLoading={previewLoading}
      reason={reason}
      onReasonChange={setReason}
      reasonLabel="Motivation (Required)"
      reasonPlaceholder="Provide a motivation for this ad-hoc interest payment. This will be reviewed by an approver."
      reasonHelperText="A motivation is mandatory for interest payment amendments on active contracts."
    >
      <Grid container spacing={2}>
        <Grid size={4}>
          <TextField
            label="Payment Date"
            type="date"
            value={formData.event_date}
            onChange={(e) => handleFieldChange('event_date', e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: contract?.deposit_date?.substring(0, 10),
              max: contract?.maturity_date?.substring(0, 10),
            }}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            fullWidth
            placeholder="e.g. Quarterly interest payment"
          />
        </Grid>
        <Grid size={4}>
          <TextField
            label="Interest Amount (Optional)"
            type="number"
            value={formData.amount}
            onChange={(e) => handleFieldChange('amount', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01', min: 0 }}
            InputProps={{
              startAdornment: contract?.currency_code
                ? <InputAdornment position="start">{contract.currency_code}</InputAdornment>
                : null,
            }}
            helperText="Leave blank to auto-calculate accrued interest up to the payment date."
          />
        </Grid>
      </Grid>
    </ContractActionDialogBase>
    <ScheduleImpactPreviewModal
      open={previewModalOpen}
      onClose={() => setPreviewModalOpen(false)}
      title="Ad-Hoc Interest Payment Impact"
      changeTypeLabel="Interest Payment"
      subtitle={`Interest payment${formData.amount !== '' ? ` of ${contract?.currency_code || ''} ${formData.amount}` : ' (auto-calculated)'} on ${formData.event_date}`}
      previewData={previewData}
      contract={contract}
    />
    </>
  );
};

export default AdhocInterestPaymentDialog;
